import { FC, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, Tooltip, Typography } from "@mui/material";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";

import { useAsu, ITrain, IMenuTreeItem, stationHasVisibleTrains } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { EquipmentStateDisplayMode, IUserDto, useUser } from "@framework/provider-user";

import Factory from "../../../../../assets/icons/factory.svg";
import Train from "../../../../../assets/icons/train.svg";
import { useOptionalAnalysisTools } from "../../../../../context/analysis-tools-context";
import { useAlarmPaletteResolver } from "../../../../../hooks/use-alarm-color";
import type { IStructureTreePoint } from "../../../../../pages/dashboard/tabs/multi-analysis/types";
import {
  getAggregateCheckboxState,
  formatStructureTreePointLabel,
} from "../../../../../pages/dashboard/tabs/multi-analysis/utils/structure-tree-utils";
import {
  collectVisibleTreeStatusRequests,
  useMenuStatusSubscriptions,
} from "../../../../../pages/subscribe/use-menu-status-subscriptions";
import { StyledTreeItem } from "../tree-item";
import { StyledTreeView } from "./styled";
import { resolveStationIconColor, resolveTrainIconColor } from "../icon-color";

export interface IListTreeView {
  stations: IMenuTreeItem[];
}

const getTrainNodeId = (trainId: number, isPointSelectionMode: boolean): string =>
  isPointSelectionMode ? `train-${trainId}` : `${trainId}`;

const parseTrainNodeId = (nodeId: string): number | null => {
  if (nodeId.startsWith("train-")) {
    const trainId = Number(nodeId.slice(6));
    return Number.isFinite(trainId) ? trainId : null;
  }

  return null;
};

const getPointNodeId = (pointId: number): string => `point-${pointId}`;

interface ITreeCheckboxLabelProps {
  title: string;
  checked: boolean;
  indeterminate?: boolean;
  compact?: boolean;
  onChange: (checked: boolean) => void;
  onLabelClick?: () => void;
}

const TreeCheckboxLabel: FC<ITreeCheckboxLabelProps> = ({
  title,
  checked,
  indeterminate,
  compact,
  onChange,
  onLabelClick,
}) => {
  const titleRef = useRef<HTMLElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) {
      return;
    }

    const updateOverflow = () => {
      setShowTooltip(el.scrollWidth > el.clientWidth + 1);
    };

    updateOverflow();

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [title]);

  return (
    <Tooltip
      title={title}
      placement="right"
      enterDelay={400}
      disableHoverListener={!showTooltip}
      disableFocusListener={!showTooltip}
      disableTouchListener={!showTooltip}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0, gap: 0 }}>
        <Checkbox
          size="small"
          checked={checked}
          indeterminate={indeterminate}
          sx={{ p: "2px", flexShrink: 0 }}
          onClick={event => event.stopPropagation()}
          onChange={event => onChange(event.target.checked)}
        />
        <Typography
          ref={titleRef}
          onClick={event => {
            event.stopPropagation();
            onLabelClick?.();
          }}
          sx={{
            fontWeight: 500,
            fontSize: compact ? 13 : 14,
            lineHeight: compact ? "15px" : "17px",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: onLabelClick ? "pointer" : "default",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const ListTreeView: FC<IListTreeView> = props => {
  const { stations } = props;

  const treeRef = useRef<HTMLUListElement | null>(null);
  const { getParentsIds, selectStation, selectTrain, selectedItem, isVisualControlOpen, tab } = useAsu();
  const analysisTools = useOptionalAnalysisTools();
  const isPointSelectionMode = Boolean(analysisTools?.isActive);
  const settings = useSettings();
  const user = useUser<IUserDto>();
  const equipmentStateDisplayMode =
    user.profile?.interfaceSettings?.equipmentStateDisplayMode ?? EquipmentStateDisplayMode.GOST;
  const tciLevels = settings.getTCILevels();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const { formatMessage } = useIntl();
  const formatPointDirectionLabel = useCallback(
    (direction: number) => formatMessage({ id: `enums.pointDirection.${direction}` }),
    [formatMessage],
  );
  const previousExpandedRef = useRef<string[]>([]);
  const selectedTrainIdCandidate = selectedItem && "TrainId" in selectedItem ? Number(selectedItem.TrainId) : null;
  const selectedTrainId =
    typeof selectedTrainIdCandidate === "number" && Number.isFinite(selectedTrainIdCandidate)
      ? selectedTrainIdCandidate
      : null;
  const resetAnalysisFilters = () => {
    analysisTools?.setToolbarState({
      orbitFilterOpen: false,
      orbitAppliedBins: [],
      orbitAppliedBandpassHz: null,
      orbitShowOriginalForm: false,
      signalAppliedHarmonics: [],
      signalAppliedBandpassHz: null,
    });
  };
  const selectAnalysisTrain = (trainId: number) => {
    if (trainId !== selectedTrainId) {
      resetAnalysisFilters();
    }

    selectTrain(trainId);
  };
  const selectedStationId =
    selectedItem && "Id" in selectedItem && typeof selectedItem.Id === "number" ? selectedItem.Id : null;
  const selectedTreeNodeId =
    selectedTrainId != null
      ? getTrainNodeId(selectedTrainId, isPointSelectionMode)
      : selectedStationId != null
        ? `${selectedStationId}`
        : "";

  const getDefaultExpanded = useCallback((): string[] => {
    if (!selectedItem) {
      return [];
    }

    const selectedIdWithParents =
      ("ParentId" in selectedItem && getParentsIds(selectedItem.ParentId).map(String)) || null;
    const selectedStationId = ("Id" in selectedItem && [`${selectedItem.Id}`]) || null;
    const selectedTrainId =
      !isPointSelectionMode && "TrainId" in selectedItem && selectedItem.TrainId
        ? [getTrainNodeId(selectedItem.TrainId, false)]
        : null;

    return selectedIdWithParents || selectedStationId || selectedTrainId || [];
  }, [getParentsIds, isPointSelectionMode, selectedItem]);

  const [expanded, setExpanded] = useState<string[]>(getDefaultExpanded());
  const visibleStatusRequests = useMemo(
    () => collectVisibleTreeStatusRequests(stations, new Set(expanded)),
    [expanded, stations],
  );
  useMenuStatusSubscriptions(visibleStatusRequests);

  const handleNodeToggle = useCallback(
    (_event: SyntheticEvent, nodeIds: string[]) => {
      const previousExpanded = new Set(previousExpandedRef.current);

      setExpanded(nodeIds);
      previousExpandedRef.current = nodeIds;

      if (!isPointSelectionMode || !analysisTools) {
        return;
      }

      nodeIds
        .filter(nodeId => !previousExpanded.has(nodeId))
        .map(parseTrainNodeId)
        .filter((trainId): trainId is number => trainId != null)
        .forEach(trainId => {
          void analysisTools.loadTrainPoints(trainId);
        });
    },
    [isPointSelectionMode, analysisTools],
  );

  const renderPoint = (point: IStructureTreePoint) => {
    if (!analysisTools) {
      return null;
    }

    const handlePointCheckboxChange = (checked: boolean) => {
      resetAnalysisFilters();

      if (checked && point.trainId && point.trainId !== selectedTrainId) {
        selectAnalysisTrain(point.trainId);
      }

      analysisTools.togglePoint(point, checked);
    };

    const handlePointLabelClick = () => {
      if (point.trainId && point.trainId !== selectedTrainId) {
        selectAnalysisTrain(point.trainId);
      }
    };

    return (
      <StyledTreeItem
        key={getPointNodeId(point.id)}
        nodeId={getPointNodeId(point.id)}
        compact
        width={0}
        height={0}
        label={
          <TreeCheckboxLabel
            title={formatStructureTreePointLabel(point, formatPointDirectionLabel)}
            checked={analysisTools.selectedPointIds.has(point.id)}
            compact
            onChange={handlePointCheckboxChange}
            onLabelClick={handlePointLabelClick}
          />
        }
      />
    );
  };

  const renderTrain = (train: ITrain) => {
    const { TrainId, TrainName } = train;

    if (!TrainId || !TrainName) {
      return null;
    }

    if (!isPointSelectionMode || !analysisTools) {
      return (
        <StyledTreeItem
          key={`${TrainId}_${TrainName}`}
          nodeId={`${TrainId}`}
          startIcon={<Train />}
          width={14}
          height={15}
          isSelected={selectedTrainId === TrainId}
          startIconColor={resolveTrainIconColor(train, {
            equipmentStateDisplayMode,
            isVisualControlOpen,
            tciLevels,
            resolveAlarmColor,
          })}
          onClick={() => selectTrain(TrainId)}
          label={
            <Typography sx={{ fontWeight: "500", fontSize: 16, lineHeight: "19px", flexGrow: 1 }}>
              {TrainName || TrainId}
            </Typography>
          }
        />
      );
    }

    const trainPoints = analysisTools.pointsByTrainId[TrainId] ?? [];
    const hasLoadedPoints = Object.prototype.hasOwnProperty.call(analysisTools.pointsByTrainId, TrainId);
    const pointIds = trainPoints.map(point => point.id);
    const aggregateCheckboxState = getAggregateCheckboxState(pointIds, analysisTools.selectedPointIds);
    const isLoadingPoints = analysisTools.loadingTrainIds.has(TrainId);

    const handleAggregateCheckboxChange = (checked: boolean) => {
      resetAnalysisFilters();

      if (checked && selectedTrainId !== TrainId) {
        selectAnalysisTrain(TrainId);
      }

      if (!pointIds.length && checked) {
        const completeSelection = analysisTools.beginTrainPointsSelection(TrainId);
        void analysisTools.loadTrainPoints(TrainId).then(completeSelection);
        return;
      }

      analysisTools.setTrainPointsSelected(trainPoints, checked);
    };

    const handleAggregateLabelClick = () => {
      if (selectedTrainId !== TrainId) {
        selectAnalysisTrain(TrainId);
      }
    };

    const renderTrainPointChildren = () => {
      if (trainPoints.length > 0) {
        return trainPoints.map(point => renderPoint(point));
      }

      return (
        <StyledTreeItem
          key={`train-${TrainId}-points-state`}
          nodeId={`train-${TrainId}-points-state`}
          compact
          width={0}
          height={0}
          label={
            isLoadingPoints ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.25, pl: 0.5 }}>
                <CircularProgress size={12} />
                <Typography sx={{ fontSize: 12, lineHeight: "14px", color: "#6a7284" }}>
                  <FormattedMessage id="form.hints.loading" />
                </Typography>
              </Box>
            ) : hasLoadedPoints ? (
              <Typography sx={{ fontSize: 12, lineHeight: "14px", color: "#6a7284", pl: 0.5, py: 0.25 }}>
                <FormattedMessage id="snackbar.pointsNotFound" />
              </Typography>
            ) : (
              <Box component="span" sx={{ display: "block", height: 0, overflow: "hidden" }} aria-hidden />
            )
          }
        />
      );
    };

    return (
      <StyledTreeItem
        key={`${TrainId}_${TrainName}`}
        nodeId={getTrainNodeId(TrainId, true)}
        startIcon={<Train />}
        width={14}
        height={15}
        compact
        expandIcon={<ChevronRight />}
        collapseIcon={<ExpandMore />}
        isSelected={selectedTrainId === TrainId}
        startIconColor={resolveTrainIconColor(train, {
          equipmentStateDisplayMode,
          isVisualControlOpen,
          tciLevels,
          resolveAlarmColor,
        })}
        onClick={event => {
          event.stopPropagation();
          selectAnalysisTrain(TrainId);
        }}
        label={
          <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <TreeCheckboxLabel
              title={TrainName || String(TrainId)}
              checked={aggregateCheckboxState === "checked"}
              indeterminate={aggregateCheckboxState === "indeterminate"}
              onChange={handleAggregateCheckboxChange}
              onLabelClick={handleAggregateLabelClick}
            />
          </Box>
        }
      >
        {renderTrainPointChildren()}
      </StyledTreeItem>
    );
  };

  const handleExpand = useCallback(
    (parentId: number | null, stationId: number) => () => {
      const newExpanded = [...expanded];

      if (parentId) {
        newExpanded.push(...getParentsIds(parentId).map(String));
      }

      newExpanded.push(stationId.toString());
      const nextExpanded = [...new Set(newExpanded)];
      previousExpandedRef.current = nextExpanded;
      setExpanded(nextExpanded);
    },
    [expanded, getParentsIds],
  );

  const handleCollapse = useCallback(
    (stationId: number) => () => {
      const newExpanded = expanded.filter(expandedId => expandedId !== stationId.toString());
      const nextExpanded = [...new Set(newExpanded)];
      previousExpandedRef.current = nextExpanded;
      setExpanded(nextExpanded);
    },
    [expanded],
  );

  const renderTree = (treeStations: IMenuTreeItem[]) => {
    return treeStations?.map((station: IMenuTreeItem) => {
      const { children, Id, Name, Trains, ParentId } = station;
      const hasChildren = stationHasVisibleTrains(station);

      if (!hasChildren) {
        return null;
      }

      return (
        <StyledTreeItem
          key={Id}
          nodeId={`${Id}`}
          startIcon={<Factory />}
          width={22}
          height={22}
          startIconColor={resolveStationIconColor(station, {
            equipmentStateDisplayMode,
            isVisualControlOpen,
            tciLevels,
            resolveAlarmColor,
          })}
          expandIcon={hasChildren ? <ChevronRight onClick={handleExpand(ParentId, Id)} /> : null}
          collapseIcon={hasChildren ? <ExpandMore onClick={handleCollapse(Id)} /> : null}
          isSelected={selectedStationId === Id}
          onClick={() => selectStation(Id)}
          label={
            <Typography sx={{ fontWeight: "500", fontSize: 16, lineHeight: "19px", flexGrow: 1 }}>{Name}</Typography>
          }
        >
          {children?.length ? renderTree(children) : null}
          {Trains?.length ? Trains?.map(renderTrain) : null}
        </StyledTreeItem>
      );
    });
  };

  useEffect(() => {
    if (selectedItem) {
      const nextExpanded = getDefaultExpanded();
      previousExpandedRef.current = nextExpanded;
      setExpanded(nextExpanded);
    }
  }, [getDefaultExpanded, selectedItem]);

  useEffect(() => {
    if (isPointSelectionMode) {
      const nextExpanded = getDefaultExpanded();
      previousExpandedRef.current = nextExpanded;
      setExpanded(nextExpanded);
    }
  }, [tab, isPointSelectionMode, getDefaultExpanded]);

  if (!stations?.length) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTop: "1px solid #EBEBF7",
        }}
      >
        <Typography fontSize={18} lineHeight="21px" fontWeight={500}>
          <FormattedMessage id="components.mainMenu.empty" />
        </Typography>
      </Box>
    );
  }

  return (
    <StyledTreeView
      ref={treeRef}
      expanded={expanded}
      selected={selectedTreeNodeId}
      onNodeToggle={handleNodeToggle}
      compact={isPointSelectionMode}
    >
      {renderTree(stations)}
    </StyledTreeView>
  );
};
